import momentTimeZone from 'moment-timezone';
import { combineReducers } from 'redux';
import { connectRouter } from 'connected-react-router';
import {
  reducer as formReducer,
  isValid as isFormValid,
  getFormValues,
} from 'redux-form';
import { get, isNil, isNull, isEmpty } from 'lodash';
import { createSelector } from 'reselect';

import { modalsReducer } from '@nextiva/crm-modals';
import {
  notificationSystemReducer,
  notificationSystemStatePath,
  imageLibraryReducer,
  imageLibraryStatePath,
  dragAndDropReducer,
  dragAndDropStatePath,
} from '@nextiva/engagement-ui';
import { SURVEY_SECTION_END } from 'sharedParts';

// @note reducer loaded from direct path for not to import all rich-text-editor ui components from index @feshchenko
// @todo load reducer from root on tree shaking enable (check tests) @feshchenko
import {
  reducerStatePath as richTextEditorStatePath,
  richTextEditorReducer,
} from '@nextiva/rich-text-editor';

import { reducers as globalStoreReducers } from '@nextiva/global-store';
import answerToDropdownOptions from 'utils/answerToDropdownOptions';
import filterAvailableLogicAnswers from 'utils/filterAvailableLogicAnswers';
import generatePostLogicRuleFormName, {
  postLogicRuleFormPrefix,
} from 'utils/generatePostLogicRuleFormName';
import isPostLogicRuleFilled from 'utils/isPostLogicRuleFilled';
import { editToolFormNamePrefix } from 'utils/generateEditToolFormName';
import {
  getAnswerTriggerOption,
  getLogicAnswerValue,
} from 'utils/questionLogicUtils';
import { inlineLabelEditorFormNamePrefix } from 'utils/generateInlineLabelEditorFormName';
import generateSurveyTakerUrl from 'utils/generateSurveyTakerUrl';

import formNames from 'config/formNames';
import postLogicTriggerValues from 'config/postLogicTriggerValues';
import { getQuestionTypeTriggerOptions as getQuestionTypeLogicTriggerOptions } from 'config/logicQuestionTriggerOptions';
import { getQuestionLogicOptions } from 'sharedParts/config/blockConfig';
import imageStorageReducer, {
  IMAGE_STORAGE_STATE_PATH,
} from 'imageStorage/reducers';

import appReducer, * as fromApp from './appReducer';
import surveyEditorReducer, * as fromEditor from './surveyEditorReducer';
import timezoneReducer, * as fromTimezone from './timezoneReducer';
import previewReducer, * as fromPreview from './surveyPreviewReducer';
import itemListReducer from './itemListReducer';
import surveyPreviewHeaderReducer, * as fromSurveyPreviewHeader from './surveyPreviewHeaderReducer';
import { editLogicToolFormNamePrefix } from '../utils/generateEditLogicToolFormName';
import stylingReducer, {
  STYLING_STATE_PATH,
} from '../domains/styling/reducers';
import previewThumbnailReducer, {
  THUMBNAIL_STATE_PATH,
} from '../domains/previewThumbnail/reducers';
import { inlineEditToolFormNamePrefix } from '../domains/blockEditor/utils/generateInlineEditToolFormName';
import {
  QUESTION_BANK_STATE_PATH,
  questionBankReducer,
} from '../domains/questionBank/reducers';

export default history =>
  combineReducers({
    ...globalStoreReducers,
    app: appReducer,
    surveyEditor: surveyEditorReducer,
    timezone: timezoneReducer,
    router: connectRouter(history),
    modals: modalsReducer,
    form: formReducer,
    preview: previewReducer,
    itemList: itemListReducer,
    surveyPreviewHeader: surveyPreviewHeaderReducer,
    [dragAndDropStatePath]: dragAndDropReducer,
    [imageLibraryStatePath]: imageLibraryReducer,
    [notificationSystemStatePath]: notificationSystemReducer,
    [richTextEditorStatePath]: richTextEditorReducer,
    [IMAGE_STORAGE_STATE_PATH]: imageStorageReducer,
    [STYLING_STATE_PATH]: stylingReducer,
    [THUMBNAIL_STATE_PATH]: previewThumbnailReducer,
    [QUESTION_BANK_STATE_PATH]: questionBankReducer,
  });

// @TODO move all selectors to relative files in root selectors folder @sinslav

export const getFormNames = state => Object.keys(state.form) || [];

export const getNewNotPersistedBlockUuidList = state =>
  fromEditor.getNewNotPersistedBlockUuidList(state.surveyEditor);

export const wasQuestionModifiedButNotPersisted = (state, uuid) =>
  fromEditor.wasModifiedButNotPersisted(state.surveyEditor, uuid);

export const getActiveEntityType = state =>
  fromEditor.getActiveEntityType(state.surveyEditor);

export const getActiveEntityCollectionPath = state =>
  fromEditor.getActiveEntityCollectionPath(state.surveyEditor);

export const getCurrentRouterRootPath = state =>
  fromEditor.getCurrentRouterRootPath(state.surveyEditor);

export const getCurrentRouterListRootPath = state =>
  fromEditor.getCurrentRouterListRootPath(state.surveyEditor);

// Timezone
export const getTimezoneOptions = state =>
  fromTimezone.getOptions(state.timezone);

// App
export const getAccessErrorType = state =>
  fromApp.getAccessErrorType(state.app);

export const hasAppAccessError = state =>
  !!fromApp.getAccessErrorType(state.app);

export const isUserInitialized = state => fromApp.isUserInitialized(state.app);

export const getUserInfo = state => fromApp.getUser(state.app);

export const getUserId = state => fromApp.getUserId(state.app);

export const getUserTimeZone = state => fromApp.getUserTimeZone(state.app);

export const getUserLicense = state => fromApp.getUserLicense(state)

// Editor App
export const getMatrixInvalidRowFormList = (state, formList) =>
  fromEditor.getMatrixInvalidRowFormList(state, formList);

export const isSomeMatrixRowFormInvalid = ({ state, formList }) =>
  fromEditor.isSomeMatrixRowFormInvalid({ state, formList });

export const getMatrixOptionCount = ({ state, form, initialValues, block }) =>
  fromEditor.getMatrixOptionCount({ state, form, initialValues, block });

export const getMatrixLabelList = ({ state, form, initialValues, block }) =>
  fromEditor.getMatrixLabelList({ state, form, initialValues, block });

export const getActiveEditorEntityId = state =>
  fromEditor.getActiveEntityId(state.surveyEditor);

export const getActiveEditorSection = state =>
  fromEditor.getActiveSection(state.surveyEditor);

export const getEditorSharedHeaderSection = state =>
  fromEditor.getSharedHeaderSection(state.surveyEditor);

export const isEditorSectionActive = (state, sectionName) =>
  fromEditor.isSectionActive(state.surveyEditor, sectionName);

export const getEditorSectionUrl = (state, sectionName) =>
  fromEditor.getSectionUrl(state.surveyEditor, sectionName);

export const getEndPageNumber = state =>
  fromEditor.getEndPageNumber(state.surveyEditor);

export const isSurveyLandingEnabled = state =>
  fromEditor.isLandingEnabled(state.surveyEditor);

export const isLandingSectionSuccessful = state =>
  fromEditor.isLandingSectionSuccessful(state.surveyEditor);

export const isQuestionsSectionSuccessful = state =>
  fromEditor.isQuestionsSectionSuccessful(state.surveyEditor);

export const isThankYouSectionSuccessful = state =>
  fromEditor.isThankYouSectionSuccessful(state.surveyEditor);

export const isThankYouSectionActive = state =>
  fromEditor.getActiveSection(state.surveyEditor) === SURVEY_SECTION_END;

export const getActiveEditorSectionPlaceholder = state =>
  fromEditor.getActiveSectionPlaceholder(state.surveyEditor);

export const isBlockTypeInSection = (state, sectionName, blockType) =>
  fromEditor.isBlockTypeInSection(state.surveyEditor, sectionName, blockType);

export const isSurveyThankYouEnabled = state =>
  fromEditor.isThankYouEnabled(state.surveyEditor);

export const isSurveyBackButtonEnabled = state =>
  fromEditor.isBackButtonEnabled(state.surveyEditor);

export const canOpenPostSurveyLogic = state =>
  fromEditor.canOpenPostSurveyLogic(state.surveyEditor);

export const getSurveyRedirectUrl = state =>
  fromEditor.getRedirectUrl(state.surveyEditor);

export const getActiveSectionOrderMap = state =>
  fromEditor.getActiveSectionOrderMap(state.surveyEditor);

export const getQuestionIdByUuid = (state, clientUuid) =>
  fromEditor.getQuestionIdByUuid(state.surveyEditor, clientUuid);

export const getBlockByUuid = (state, clientUuid) =>
  fromEditor.getBlockByUuid(state.surveyEditor, clientUuid);

export const getQuestionsSectionBlocks = state =>
  fromEditor.getQuestionsSectionBlocks(state.surveyEditor);

export const getBlockOptionIdByIndex = (state, clientUuid, optionIndex) =>
  fromEditor.getBlockOptionIdByIndex(
    state.surveyEditor,
    clientUuid,
    optionIndex,
  );

export const getCachedBlockData = (state, clientUuid) =>
  fromEditor.getCachedBlockData(state.surveyEditor, clientUuid);

export const isUniqueBlock = (state, clientUuid) =>
  fromEditor.isUniqueBlock(state.surveyEditor, clientUuid);

export const isUniqueBlockType = (state, sectionName, blockType) =>
  fromEditor.isUniqueBlockType(state.surveyEditor, sectionName, blockType);

export const isSavedBlock = (state, clientUuid) =>
  fromEditor.isSavedBlock(state.surveyEditor, clientUuid);

export const getThumbnailPageNumber = state =>
  fromEditor.getThumbnailPageNumber(state.surveyEditor);

export const isFullWidthSurvey = state =>
  fromEditor.isFullWidthSurvey(state.surveyEditor);

export const isSetOverwriteCustomStylesEnabled = state =>
  fromEditor.isSetOverwriteCustomStylesEnabled(state.surveyEditor);

export const isBlockChangingToolsEnabled = (state, clientUuid) =>
  fromEditor.isBlockChangingToolsEnabled(state.surveyEditor, clientUuid);

export const getSectionActiveBlockClientUuid = state =>
  fromEditor.getActiveBlockClientUuid(state.surveyEditor);

export const isSectionBlockActive = (state, clientUuid) =>
  fromEditor.isBlockActive(state.surveyEditor, clientUuid);

export const isQuestionTextInvalid = (state, clientUuid) =>
  fromEditor.isQuestionTextInvalid(state.surveyEditor, clientUuid);

export const getSectionLatestAddedBlockClientUuid = state =>
  fromEditor.getLatestAddedBlockClientUuid(state.surveyEditor);

export const getSectionLatestRemovedBlockClientUuid = state =>
  fromEditor.getLatestRemovedBlockClientUuid(state.surveyEditor);

export const isAddBlockNetworkPending = state =>
  fromEditor.isAddBlockNetworkPending(state.surveyEditor);

export const getUnusedRespondentInfoMorphOptionList = state =>
  fromEditor.getUnusedRespondentInfoMorphOptionList(state.surveyEditor);

export const getNetworkPendingBlockList = state =>
  fromEditor.getNetworkPendingBlockList(state.surveyEditor);

export const getNetworkPendingOrderMap = state =>
  fromEditor.getNetworkPendingOrderMap(state.surveyEditor);

export const isMoveBlockNetworkPending = state =>
  fromEditor.isMoveBlockNetworkPending(state.surveyEditor);

export const canMoveSurveyEditorBlock = (state, clientUuid, newIndex) =>
  fromEditor.canMoveBlock(state.surveyEditor, clientUuid, newIndex);

export const canMoveSurveyEditorBlockUp = (state, clientUuid) =>
  fromEditor.canMoveBlockUp(state.surveyEditor, clientUuid);

export const canMoveSurveyEditorBlockDown = (state, clientUuid) =>
  fromEditor.canMoveBlockDown(state.surveyEditor, clientUuid);

export const getEditToolFormNames = state =>
  getFormNames(state).filter(name => name.startsWith(editToolFormNamePrefix));

export const getInlineEditToolFormNames = state =>
  getFormNames(state).filter(name =>
    name.startsWith(inlineEditToolFormNamePrefix),
  );

export const getLogicEditToolFormNames = state =>
  getFormNames(state).filter(name =>
    name.startsWith(editLogicToolFormNamePrefix),
  );

export const getInlineLabelEditorFormNames = state =>
  getFormNames(state).filter(name =>
    name.startsWith(inlineLabelEditorFormNamePrefix),
  );

export const getBlockIndexInActiveSection = (state, clientUuid) =>
  fromEditor.getBlockIndexInActiveSection(state.surveyEditor, clientUuid);

export const getActiveSectionBlockUuidByIndex = (state, index) =>
  fromEditor.getActiveSectionBlockUuidByIndex(state.surveyEditor, index);

export const getEditorSurveyName = state =>
  fromEditor.getSurveyName(state.surveyEditor);

export const isSurveyNameFocused = state =>
  fromEditor.isSurveyNameFocused(state.surveyEditor);

export const getEditorEntityId = state =>
  fromEditor.getEntityId(state.surveyEditor);

export const getEditorEntityState = state =>
  fromEditor.getEntityStatus(state.surveyEditor);

export const getOrderMapBySection = (state, sectionName) =>
  fromEditor.getOrderMapBySection(sectionName)(state.surveyEditor);

export const getEditSurveyStatus = state =>
  fromEditor.getSurveyStatus(state.surveyEditor);

export const isEditSurveyClosed = state =>
  fromEditor.isSurveyClosed(state.surveyEditor);

export const shouldRestrictAddBlockToSurvey = state =>
  fromEditor.shouldRestrictAddBlockToSurvey(state.surveyEditor);

export const shouldRestrictChangeGlobalStyling = state =>
  fromEditor.shouldRestrictChangeGlobalStyling(state.surveyEditor);

export const getEditSurveySetting = (state, name) =>
  fromEditor.getSurveySetting(state.surveyEditor, name);

export const isUpdateFrequencyEnabled = state =>
  fromEditor.isUpdateFrequencyEnabled(state.surveyEditor);

export const getQuestionNumberingEnabled = state =>
  fromEditor.getQuestionNumberingEnabled(state.surveyEditor);

export const getSurveyUpdateFrequency = state =>
  fromEditor.getSurveyUpdateFrequency(state.surveyEditor);

export const isSurveyEditorLoading = state =>
  fromEditor.isEditorLoading(state.surveyEditor);

export const getBlockType = (state, clientUuid) =>
  fromEditor.getBlockType(state.surveyEditor, clientUuid);

export const getEnabledSectionsPageToQuestionListMap = state =>
  fromEditor.getEnabledSectionsPageToQuestionListMap(state.surveyEditor);

export const isQuestionsSectionOnePage = state =>
  fromEditor.isQuestionsSectionOnePage(state.surveyEditor);

export const getActiveSectionBlocks = state =>
  fromEditor.getActiveSectionBlocks(state.surveyEditor);

export const getActiveSectionPages = state =>
  fromEditor.getActiveSectionPages(state.surveyEditor);

export const getCurrentUserTime = state => {
  const userTimeZone = getUserTimeZone(state);

  return momentTimeZone.tz(userTimeZone).format();
};

export const getQuestionType = (state, clientUuid) =>
  fromEditor.getQuestionType(state.surveyEditor, clientUuid);

export const getQuestionById = (state, id) =>
  fromEditor.getQuestionById(state.surveyEditor, id);

export const isQuestionLogicValid = (state, clientUuid) =>
  fromEditor.isQuestionLogicValid(state.surveyEditor, clientUuid);

export const isQuestionLogicActionShow = (state, clientUuid) =>
  fromEditor.isQuestionLogicActionShow(state.surveyEditor, clientUuid);

export const isQuestionLogicActionSkip = (state, clientUuid) =>
  fromEditor.isQuestionLogicActionSkip(state.surveyEditor, clientUuid);

export const hasPrevBlockShowLogic = (state, clientUuid) =>
  fromEditor.hasPrevBlockShowLogic(state.surveyEditor, clientUuid);

export const isActiveBlockLogicPanelOpened = state =>
  fromEditor.isActiveBlockLogicPanelOpened(state.surveyEditor);

export const isQuestionUsedInOtherQuestionLogic = (state, clientUuid) =>
  fromEditor.isQuestionUsedInOtherQuestionLogic(state.surveyEditor, clientUuid);

export const getNextBlockUuidInActiveSection = (state, clientUuid) =>
  fromEditor.getNextBlockUuidInActiveSection(state.surveyEditor, clientUuid);

export const doesLastQuestionHaveValidLogic = state =>
  fromEditor.doesLastQuestionHaveValidLogic(state.surveyEditor);

export const getActiveSectionBlockMaxAvailableIndex = state =>
  fromEditor.getActiveSectionBlockMaxAvailableIndex(state.surveyEditor);

export const getPostLogicAvailableQuestions = (state, itemId) =>
  fromEditor.getPostLogicAvailableQuestions(state.surveyEditor, itemId);

export const getSurveyPreviewSectionLastPageNumber = state =>
  fromPreview.getSurveySectionLastPageNumber(state.preview);

export const isPreviewLandingCurrentSection = state =>
  fromPreview.isLandingCurrentSection(state.preview);

export const hasPreviewQuestionsSectionOnlyOnePage = state =>
  fromPreview.hasSurveyQuestionsSectionOnlyOnePage(state.preview);

export const getPageToStartPreview = state =>
  fromEditor.getPageToStartPreview(state.surveyEditor);

export const getSettingsCutOffFormTimezone = state => {
  const formData = getFormValues(formNames.cutOffDate)(state);
  return formData ? formData.timezone : null;
};

export const getSettingsCutOffDateCurrentVisibleMonth = state => {
  const formData = getFormValues(formNames.cutOffDate)(state);
  return formData ? formData.selectedMonth : null;
};

export const getSurveySlug = state =>
  fromEditor.getSurveySlug(state.surveyEditor);

export const getSurveyTakerUrl = state =>
  generateSurveyTakerUrl(getSurveySlug(state));

export const getLogicSuggestions = (state, currentQuestionUuid) =>
  fromEditor.getLogicSuggestions(state.surveyEditor, currentQuestionUuid);

export const getQuestionLogic = (state, questionUuid) =>
  fromEditor.getQuestionLogic(state.surveyEditor, questionUuid);

export const getQuestionLogicList = (state, currentQuestionUuid) =>
  fromEditor.getQuestionLogicList(state.surveyEditor, currentQuestionUuid);

export const getQuestionRegardlessLogicUuid = (state, currentQuestionUuid) =>
  fromEditor.getQuestionRegardlessLogicUuid(
    state.surveyEditor,
    currentQuestionUuid,
  );

export const getQuestionOptionsById = (state, questionId) =>
  fromEditor.getQuestionOptionsById(state.surveyEditor, questionId);

export const getQuestionOptionsByUuid = (state, questionUuid) =>
  fromEditor.getQuestionOptionsByUuid(state.surveyEditor, questionUuid);

export const getEditorOrderedBlockList = state =>
  fromEditor.getOrderedBlockList(state.surveyEditor);

export const getQuestionOptionsByFormName = (state, formName) => {
  return get(getFormValues(formName)(state), 'options', null);
};

export const getQuestionOptionCount = (state, formName) => {
  return get(getQuestionOptionsByFormName(state, formName), 'length', null);
};

export const isQuestionOptionMaxLimitReached = (state, formName, limit) => {
  return getQuestionOptionCount(state, formName) >= limit;
};

export const isQuestionOptionMinLimitReached = (state, formName, limit) => {
  return getQuestionOptionCount(state, formName) <= limit;
};

// Question logic && Post survey logic
const getQuestionLogicTriggerOptions = (state, questionId, decorated = true) =>
  getQuestionTypeLogicTriggerOptions(
    getQuestionType(state, questionId),
    decorated,
  );

export const getScaleLogicAvailableAnswers = (
  state,
  { questionUuid, logicOptionIndex, trigger = null },
) => {
  // @todo use reselect here @lukavyi @apavlosiuk
  const logicList = getQuestionLogicList(state, questionUuid);
  if (isNull(trigger)) {
    trigger = getAnswerTriggerOption(logicList[logicOptionIndex]);
  }

  const setUpTriggerRules = logicList
    .filter((rule, index) => index !== logicOptionIndex)
    .filter(
      rule => getAnswerTriggerOption(rule) && !isNil(getLogicAnswerValue(rule)),
    )
    .map(rule => ({
      trigger: getAnswerTriggerOption(rule),
      answer: getLogicAnswerValue(rule),
    }));

  const questionOptions = getQuestionLogicOptions(
    getBlockByUuid(state, questionUuid),
  );

  return filterAvailableLogicAnswers(
    setUpTriggerRules,
    questionOptions,
    trigger,
  );
};

export const doesLogicHaveAvailableAnswers = (
  state,
  { questionUuid, logicOptionIndex, trigger },
) =>
  getScaleLogicAvailableAnswers(state, {
    questionUuid,
    logicOptionIndex,
    trigger,
  }).length > 0;

export const getLogicAvailableTriggers = (
  state,
  { questionUuid, logicOptionIndex },
) => {
  const questionId = getQuestionIdByUuid(state, questionUuid);
  const triggerList = getQuestionLogicTriggerOptions(state, questionId, false);

  return triggerList.filter(triggerOption =>
    doesLogicHaveAvailableAnswers(state, {
      questionUuid,
      logicOptionIndex,
      trigger: triggerOption.value,
    }),
  );
};

// Post survey logic
export const getPostLogicItemIds = state =>
  fromEditor.getPostLogicItemIds(state.surveyEditor);

export const getPostLogicRules = (state, itemId) =>
  fromEditor.getPostLogicRules(state.surveyEditor, itemId);

export const getPostLogicPersistedRulesCount = (state, itemId) =>
  fromEditor.getPostLogicPersistedRulesCount(state.surveyEditor, itemId);

export const getPostLogicRule = (state, clientId) =>
  fromEditor.getPostLogicRule(state.surveyEditor, clientId);

export const getPostLogicQuestionId = (state, itemId) =>
  fromEditor.getPostLogicQuestionId(state.surveyEditor, itemId);

export const isPostLogicItemExpanded = (state, itemId) =>
  fromEditor.isPostLogicItemExpanded(state.surveyEditor, itemId);

export const getPostLogicRuleClientIds = (state, itemId) =>
  fromEditor.getPostLogicRuleClientIds(state.surveyEditor, itemId);

export const getPostLogicRulePersistenceId = (state, clientId) =>
  fromEditor.getPostLogicRulePersistenceId(state.surveyEditor, clientId);

export const getActiveSurveyFirstPageBlockList = state =>
  fromEditor.getFirstPageBlockList(state.surveyEditor);

export const getPostLogicFormRule = (state, clientId) => ({
  ...getFormValues(generatePostLogicRuleFormName(clientId))(state),
  clientId,
  id: getPostLogicRulePersistenceId(state, clientId),
});

export const getPostLogicFormRules = (state, itemId) =>
  getPostLogicRuleClientIds(state, itemId)
    .map(clientId => getPostLogicFormRule(state, clientId))
    .filter(values => !!values);

export const isPostLogicRuleFormSubmitAllowed = (state, clientId) => {
  return (
    isPostLogicRuleFilled(getPostLogicFormRule(state, clientId)) ||
    isPostLogicRuleFilled(getPostLogicRule(state, clientId))
  );
};

export const isPostLogicRuleFormAnswerVisible = (state, clientId) =>
  !!getPostLogicFormRule(state, clientId).trigger;

export const isPostLogicRuleFormPointsVisible = (state, clientId) =>
  !!getPostLogicFormRule(state, clientId).action;

export const getPostLogicAvailableAnswers = (
  state,
  { itemId, clientId, trigger },
) => {
  const setUpTriggerRules = getPostLogicFormRules(state, itemId)
    .filter(rule => rule.clientId !== clientId)
    .filter(rule => rule.trigger && !isNil(rule.answer));

  const postLogicQuestionId = getPostLogicQuestionId(state, itemId);
  const postLogicQuestion = getQuestionById(state, postLogicQuestionId);
  const questionOptions = getQuestionOptionsById(
    state,
    postLogicQuestionId,
  ).map((answer, index) =>
    answerToDropdownOptions(answer, index, postLogicQuestion.startFromZero),
  );

  return filterAvailableLogicAnswers(
    setUpTriggerRules,
    questionOptions,
    trigger,
  );
};

export const getPostLogicRuleFormNames = state =>
  getFormNames(state).filter(formName =>
    formName.includes(postLogicRuleFormPrefix),
  );

export const getPostLogicItemRemaningAnswersCount = (state, itemId) =>
  getPostLogicAvailableAnswers(state, {
    itemId,
    trigger: postLogicTriggerValues.equal,
  }).length;

export const getPostLogicAvailableTriggers = (state, { itemId, clientId }) => {
  return getQuestionLogicTriggerOptions(
    state,
    getPostLogicQuestionId(state, itemId),
  ).filter(
    triggerOption =>
      getPostLogicAvailableAnswers(state, {
        itemId,
        clientId,
        trigger: triggerOption.value,
      }).length,
  );
};

export const isPostLogicReceived = state =>
  fromEditor.isPostLogicReceived(state.surveyEditor);

export const getPostLogicConfiguredQuestionsCount = state =>
  fromEditor.getPostLogicConfiguredQuestionsCount(state.surveyEditor);

// Survey name
export const isSurveyNameFormValid = createSelector(
  isFormValid(formNames.changeSurveyName),
  isFormValid(formNames.repeatVisitorsMessage),
  isFormValid(formNames.blockedUserMessage),
  isFormValid(formNames.redirect),
  isFormValid(formNames.maxResponsesCount),
  (...args) => args.every(Boolean),
);

export const isSurveyDataValid = state => isSurveyNameFormValid(state);

export const isNavigationDisabled = state => !isSurveyDataValid(state);

export const getEditorRawSurveyData = state =>
  fromEditor.getRawSurveyData(state.surveyEditor);

export const getEditorHeaderEndEnabled = state =>
  fromEditor.getHeaderEndEnabled(state.surveyEditor);

export const getEditorHeaderLandingEnabled = state =>
  fromEditor.getHeaderLandingEnabled(state.surveyEditor);

export const getEditorHeaderQuestionsEnabled = state =>
  fromEditor.getHeaderQuestionsEnabled(state.surveyEditor);

export const getEditorHeaderEnabled = (state, sectionName) =>
  fromEditor.getHeaderEnabled(state.surveyEditor, sectionName);

// Survey Preview
export const getSurveyPreviewCurrentPage = state =>
  fromPreview.getCurrentPage(state.preview);

export const getSurveyPreviewLatestVisitedPageNumber = state =>
  fromPreview.getLatestVisitedPageNumber(state.preview);

export const getSurveyPreviewVisitedQuestions = state =>
  fromPreview.getVisitedQuestions(state.preview);

export const getSurveyPreviewVisitedPages = state =>
  fromPreview.getVisitedPages(state.preview);

export const getSurveyPreviewNextPage = state =>
  fromPreview.getNextPageNumber(state.preview);

export const getSurveyPreviewPendingRedirectIdByNextPageNumber = (
  state,
  nextPage,
) => fromPreview.getPendingRedirectIdByNextPageNumber(state.preview, nextPage);

export const getSurveyPreviewPendingRedirectToBlockId = state =>
  fromPreview.getPendingRedirectToBlockId(state.preview);

export const isSurveyPreviewQuestionLogicValid = (state, questionId) =>
  fromPreview.isQuestionLogicValid(state.preview, questionId);

export const getSurveyPreviewQuestionLogicRedirectBlockId = (
  state,
  questionId,
  answer,
) =>
  fromPreview.getQuestionLogicRedirectBlockId(
    state.preview,
    questionId,
    answer,
  );

export const getSurveyPreviewBlockList = state =>
  fromPreview.getBlockList(state.preview);

export const hasSurveyPreviewQuestionsSectionOnlyOnePage = state =>
  fromPreview.hasSurveyQuestionsSectionOnlyOnePage(state.preview);

export const shouldSurveyPreviewRenderPoweredBy = state =>
  fromPreview.shouldSurveyPreviewRenderPoweredBy(state.preview);

export const getSurveyPreviewCurrentBlockList = state =>
  fromPreview.getCurrentPageBlockList(state.preview);

export const isSurveyPreviewFirstPage = state =>
  fromPreview.isSurveyFirstPage(state.preview);

export const isSurveyPreviewLastPage = state =>
  fromPreview.isLastPage(state.preview);

export const getSurveyPreviewDisplayPage = (state, pageNumber) =>
  fromPreview.getDisplayPage(state.preview, pageNumber);

export const isSurveyPreviewFullWidth = state =>
  fromPreview.isSurveyFullWidth(state.preview);

export const getSurveyPreviewCurrentSectionHeaderBackgroundCoords = state =>
  fromPreview.getCurrentSectionHeaderBackgroundCoords(state.preview);

export const isSurveyPreviewEndEnabled = state =>
  fromPreview.isEndEnabled(state.preview);

export const isSurveyPreviewBlockNextOrSubmitButton = (state, id) =>
  fromPreview.isBlockNextOrSubmitButton(state.preview, id);

export const getSurveyPreviewFirstInvalidFormName = state =>
  fromPreview.getFirstInvalidFormName(state.preview);

export const isAllSurveyPreviewPageAnswersValid = state =>
  fromPreview.isAllPageAnswersValid(state.preview);

export const getSurveyPreviewRedirectUrl = state =>
  fromPreview.getRedirectUrl(state.preview);

export const getSurveyPreviewCurrentSectionName = state =>
  fromPreview.getCurrentSectionName(state.preview);

export const getSurveyPreviewInvalidFormNameToScroll = state =>
  fromPreview.getInvalidFormNameToScroll(state.preview);

export const isSurveyPreviewEmpty = createSelector(
  isSurveyEditorLoading,
  getSurveyPreviewBlockList,
  (isLoading, blockList) => !isLoading && isEmpty(blockList),
);

export const getSurveyPreviewVisitedPagesLength = state =>
  fromPreview.getVisitedPagesLength(state.preview);

// Modals
export const getModalsState = state => state.modals;

export const hasEditorSectionHeader = (state, sectionName) =>
  fromEditor.hasSectionHeader(state.surveyEditor, sectionName);

export const isActiveSectionHeaderExist = state =>
  fromEditor.isActiveSectionHeaderExist(state.surveyEditor);

export const getEditorHeaderData = state =>
  fromEditor.getHeaderData(state.surveyEditor);

export const getEditorSectionHeader = (state, sectionName) =>
  fromEditor.getSectionHeader(state.surveyEditor, sectionName);

export const hasPersistedSectionHeader = (state, sectionName) =>
  fromEditor.hasPersistedSectionHeader(state.surveyEditor, sectionName);

export const getActiveSectionHeaderBackgroundImageUrl = state =>
  fromEditor.getActiveSectionHeaderBackgroundImageUrl(state.surveyEditor);

export const getActiveSectionHeaderLogoImageSizePreset = state =>
  fromEditor.getActiveSectionHeaderLogoImageSizePreset(state.surveyEditor);

export const getActiveSectionHeaderLogoImageAlign = state =>
  fromEditor.getActiveSectionHeaderLogoImageAlign(state.surveyEditor);

export const getActiveSectionHeaderLogoWithTextAlign = state =>
  fromEditor.getActiveSectionHeaderLogoWithTextAlign(state.surveyEditor);

export const getActiveSectionHeaderBackgroundImageSize = state =>
  fromEditor.getActiveSectionHeaderBackgroundImageSize(state.surveyEditor);

export const getActiveSectionHeaderHeight = state =>
  fromEditor.getActiveSectionHeaderHeight(state.surveyEditor);

export const getActiveSectionHeaderBackgroundColor = state =>
  fromEditor.getActiveSectionHeaderBackgroundColor(state.surveyEditor);

export const isActiveSectionHeaderFullWidth = state =>
  fromEditor.isActiveSectionHeaderFullWidth(state.surveyEditor);

export const isEditorSharedHeaderFullWidth = state =>
  fromEditor.isSharedHeaderFullWidth(state.surveyEditor);

export const getActiveSectionHeaderBackgroundCoords = state =>
  fromEditor.getActiveSectionHeaderBackgroundCoords(state.surveyEditor);

export const doesActiveSectionHasHeader = state =>
  fromEditor.doesActiveSectionHasHeader(state.surveyEditor);

export const getHeaderPageBoxTypeBySection = (state, pageIndex, section) =>
  fromEditor.getHeaderPageBoxType(state.surveyEditor, pageIndex, section);

export const isActiveSectionHeaderContentEnabled = state =>
  fromEditor.isActiveSectionHeaderContentEnabled(state.surveyEditor);

export const getActiveSectionHeaderEnabled = state =>
  fromEditor.getHeaderEnabled(
    state.surveyEditor,
    fromEditor.getActiveSection(state.surveyEditor),
  );

export const getPreviewCurrentSectionHeaderBackgroundImageUrl = state =>
  fromPreview.getCurrentSectionHeaderBackgroundImageUrl(state.preview);

export const getPreviewCurrentSectionHeaderBackgroundImageSize = state =>
  fromPreview.getCurrentSectionHeaderBackgroundImageSize(state.preview);

export const getPreviewCurrentSectionHeaderLogoImageUrl = state =>
  fromPreview.getCurrentSectionHeaderLogoImageUrl(state.preview);

export const getPreviewCurrentSectionHeaderLogoImageAlign = state =>
  fromPreview.getCurrentSectionHeaderLogoImageAlign(state.preview);

export const getPreviewCurrentSectionHeaderLogoWithTextAlign = state =>
  fromPreview.getCurrentSectionHeaderLogoWithTextAlign(state.preview);

export const getPreviewCurrentSectionHeaderHeight = state =>
  fromPreview.getCurrentSectionHeaderHeight(state.preview);

export const getPreviewCurrentSectionHeaderBackgroundColor = state =>
  fromPreview.getCurrentSectionHeaderBackgroundColor(state.preview);

export const isPreviewCurrentSectionHeaderFullWidth = state =>
  fromPreview.isCurrentSectionHeaderFullWidth(state.preview);

export const isPreviewCurrentSectionHeaderPlaceholder = state =>
  fromPreview.isCurrentSectionHeaderPlaceholder(state.preview);

export const getPreviewCurrentSectionHeaderPageBoxType = state =>
  fromPreview.getCurrentSectionHeaderPageBoxType(state.preview);

export const isPreviewCurrentSectionHeaderContentEnabled = state =>
  fromPreview.isCurrentSectionHeaderContentEnabled(state.preview);

export const getPreviewCurrentSectionHeaderLogoImageLinkUrl = state =>
  fromPreview.getCurrentSectionHeaderLogoImageLinkUrl(state.preview);

export const getPreviewCurrentSectionHeaderLogoImageSizePreset = state =>
  fromPreview.getCurrentSectionHeaderLogoImageSizePreset(state.preview);

export const getPreviewCurrentSectionHeaderLogoImageSize = state =>
  fromPreview.getCurrentSectionHeaderLogoImageSize(state.preview);

export const getPreviewCurrentSectionHeaderLayout = state =>
  fromPreview.getCurrentSectionHeaderLayout(state.preview);

export const getPreviewCurrentSectionHeaderTextContent = state =>
  fromPreview.getCurrentSectionHeaderTextContent(state.preview);

export const getPreviewCurrentSectionHeaderClientUuid = state =>
  fromPreview.getCurrentSectionHeaderClientUuid(state.preview);

export const getPreviewCurrentSectionHeaderEnabled = state =>
  fromPreview.getCurrentSectionHeaderEnabled(state.preview);

export const isActiveSectionEmpty = state =>
  getActiveSectionBlocks(state).length === 0 &&
  !doesActiveSectionHasHeader(state);

export const getActiveSurveySettings = state =>
  fromEditor.getSurveySettings(state.surveyEditor);

export const isEditSurveyStatusPending = state =>
  fromEditor.isStatusPending(state.surveyEditor);

// Survey Preview Header
export const hasSurveyPreviewEditorHeader = state =>
  fromSurveyPreviewHeader.hasEditorHeader(state.surveyPreviewHeader);

export const getActiveSectionHeaderLogoImageLinkUrl = state =>
  fromEditor.getActiveSectionHeaderLogoImageLinkUrl(state.surveyEditor);

export const isActiveSectionRequiredBlockNeeded = state =>
  fromEditor.isActiveSectionRequiredBlockNeeded(state.surveyEditor);

export const getActiveSectionHeaderLayout = state =>
  fromEditor.getActiveSectionHeaderLayout(state.surveyEditor);

// All in one preview
export const getHeaderClientUuidBySection = (state, section) =>
  fromEditor.getSectionHeaderClientUuid(state.surveyEditor, section);

export const isHeaderPlaceholderBySection = (state, section) =>
  fromEditor.isSectionHeaderPlaceholder(state.surveyEditor, section);

export const getHeaderTextContentBySection = (state, section) =>
  fromEditor.getSectionHeaderTextContent(state.surveyEditor, section);

export const getHeaderLayoutBySection = (state, section) =>
  fromEditor.getSectionHeaderLayout(state.surveyEditor, section);

export const getHeaderBackgroundColorBySection = (state, headerSection) =>
  fromEditor.getSectionHeaderBackgroundColor(state.surveyEditor, headerSection);

export const getHeaderBackgroundCoordsBySection = (state, headerSection) =>
  fromEditor.getSectionHeaderBackgroundCoords(
    state.surveyEditor,
    headerSection,
  );

export const getHeaderBackgroundImageSizeBySection = (state, headerSection) =>
  fromEditor.getSectionHeaderBackgroundImageSize(
    state.surveyEditor,
    headerSection,
  );

export const getHeaderBackgroundImageUrlBySection = (state, headerSection) =>
  fromEditor.getSectionHeaderBackgroundImageUrl(
    state.surveyEditor,
    headerSection,
  );

export const getHeaderHeightBySection = (state, headerSection) =>
  fromEditor.getSectionHeaderHeight(state.surveyEditor, headerSection);

export const getHeaderLogoImageAlignBySection = (state, headerSection) =>
  fromEditor.getSectionHeaderLogoImageAlign(state.surveyEditor, headerSection);

export const getHeaderLogoWithTextAlignBySection = (state, headerSection) =>
  fromEditor.getSectionHeaderLogoWithTextAlign(
    state.surveyEditor,
    headerSection,
  );

export const getHeaderLogoImageLinkUrlBySection = (state, headerSection) =>
  fromEditor.getSectionHeaderLogoImageLinkUrl(
    state.surveyEditor,
    headerSection,
  );

export const getHeaderLogoImageSizeBySection = (state, headerSection) =>
  fromEditor.getSectionHeaderLogoImageSize(state.surveyEditor, headerSection);

export const getHeaderLogoImageSizePresetBySection = (state, headerSection) =>
  fromEditor.getSectionHeaderLogoImageSizePreset(
    state.surveyEditor,
    headerSection,
  );

export const getHeaderLogoImageUrlBySection = (state, headerSection) =>
  fromEditor.getSectionHeaderLogoImageUrl(state.surveyEditor, headerSection);

export const isHeaderFullWidthBySection = (state, headerSection) =>
  fromEditor.isSectionHeaderFullWidth(state.surveyEditor, headerSection);

export const isHeaderContentEnabledBySection = (state, headerSection) =>
  fromEditor.isSectionHeaderContentEnabled(state.surveyEditor, headerSection);

export const getEditorSharedHeader = state =>
  fromEditor.getEditorSharedHeader(state.surveyEditor);

export const hasEditorSharedHeader = state =>
  fromEditor.hasEditorSharedHeader(state.surveyEditor);
